import React from 'react';
import { useDetailViewState } from '../detail/DetailReturnState';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../../../../api/api';
import { useUserStore } from '../../../../stores/userStore';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { useColorScheme } from '../../../../contexts/ColorSchemeContext';
import baseline, { appTextStyle } from '../../../../app-ui/baseline';
import './HistoricalReviews.css';

export default function HistoricalReviews({ albumId }) {
  const { user } = useUserStore();
  // Native keys this section by account and album, so a replacement starts collapsed.
  return user?.id && albumId ? (
    <HistoryContent key={`${user.id}:${albumId}`} albumId={albumId} userId={user.id} />
  ) : null;
}

function HistoryContent({ albumId, userId }) {
  const { locale } = useWebLocale();
  const { resolvedMode } = useColorScheme();
  const en = locale === 'en';
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const [open, setOpen] = useDetailViewState(`history:${userId}:${albumId}`, false);
  const query = useQuery({
    queryKey: ['album-review-history', userId, albumId],
    enabled: open,
    staleTime: 0,
    queryFn: async ({ signal }) =>
      (await api.getAuth(`/albums/${albumId}/review-history`, { signal })).data.data,
  });
  const label = en ? 'My earlier records before catalog merging' : '공식 편입 전 내 이전 기록';
  return (
    <section
      className="pp-review-history"
      aria-label={label}
      style={{ '--history-text': colors.text, '--history-secondary': colors.textSecondary }}
    >
      <button
        type="button"
        className="pp-review-history-toggle"
        style={appTextStyle(baseline.contentText.metadata)}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        {label}
      </button>
      {open && (
        <>
          <p className="pp-review-history-note" style={appTextStyle(baseline.contentText.metadata)}>
            {en
              ? 'Earlier reviews and reactions are preserved, but excluded from the current average.'
              : '이전 리뷰와 반응은 보존되며 현재 평균에는 포함되지 않습니다.'}
          </p>
          {query.isLoading && (
            <div
              className="pp-review-history-loading"
              role="status"
              aria-label={en ? 'Loading…' : '불러오는 중…'}
            >
              <CircularProgress size={20} color="inherit" aria-hidden="true" />
            </div>
          )}
          {query.isError && (
            <button
              type="button"
              className="pp-review-history-retry"
              style={appTextStyle(baseline.contentText.label)}
              onClick={() => void query.refetch()}
            >
              {en ? 'Could not load. Retry' : '불러오지 못했습니다. 다시 시도'}
            </button>
          )}
          {!query.isLoading && !query.isError && query.data?.length === 0 && (
            <p
              className="pp-review-history-note"
              style={appTextStyle(baseline.contentText.metadata)}
            >
              {en ? 'No earlier records.' : '이전 기록이 없습니다.'}
            </p>
          )}
          {query.data?.map((record) => {
            const Record = record.albumSlug ? Link : 'button';
            return (
              <Record
                className="pp-review-history-record"
                key={record.id}
                {...(record.albumSlug
                  ? {
                      to: `/reviews/${encodeURIComponent(record.albumSlug)}/user-reviews/${record.id}`,
                    }
                  : { type: 'button', disabled: true })}
              >
                <span style={appTextStyle(baseline.contentText.metadata)}>
                  {record.rating.toFixed(1)} · {record.createdAt.slice(0, 10)}
                </span>
                <span
                  className="pp-review-history-body"
                  style={appTextStyle(baseline.contentText.body)}
                >
                  {record.content || (en ? 'Rating record' : '별점 기록')}
                </span>
              </Record>
            );
          })}
        </>
      )}
    </section>
  );
}
